import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DecisionSubViews, DecisionViews } from 'pages/Decision/constants'
import { ApplicationProgramCode } from 'share/types'
import type { PropertyUnit } from 'share/types/api/pledge'

import type { RootState } from '..'

export type LoanParams = {
  amount?: string
  period?: string
}

type InitialState = {
  mainView?: DecisionViews
  subView?: DecisionSubViews
  loanParams?: LoanParams
  program?: ApplicationProgramCode
  property?: PropertyUnit
  creditProgramID?: string
  okedID?: string
  insuranceSum?: string
  creditSum?: string
}

const initialState: InitialState = {
  mainView: DecisionViews.PROGRAMS,
  loanParams: {}
}

const decisionSlice = createSlice({
  name: 'decisionSlice',
  initialState,
  reducers: {
    setProgram(state, { payload }: PayloadAction<ApplicationProgramCode>) {
      state.program = payload
    },
    setCreditProgramID(state, { payload }: PayloadAction<string>) {
      state.creditProgramID = payload
    },
    setMainView(state, { payload }: PayloadAction<DecisionViews>) {
      state.mainView = payload
    },
    setSubView(state, { payload }: PayloadAction<DecisionSubViews>) {
      state.subView = payload
    },
    setOkedID(state, { payload }: PayloadAction<string>) {
      state.okedID = payload
    },
    setLoanParams: (state, { payload: { amount, period } }: PayloadAction<LoanParams>) => {
      state.loanParams = {
        amount: amount ?? state.loanParams?.amount,
        period: period ?? state.loanParams?.period
      }
    },
    setProperty: (state, { payload }: PayloadAction<PropertyUnit | undefined>) => {
      state.property = payload
    },
    setInsuranseData: (state, { payload }: PayloadAction<{ insurance_sum: string; credit_sum: string }>) => {
      state.insuranceSum = payload.insurance_sum
      state.creditSum = payload.credit_sum
    }
  }
})

export const {
  setMainView,
  setLoanParams,
  setProgram,
  setCreditProgramID,
  setSubView,
  setInsuranseData,
  setProperty,
  setOkedID
} = decisionSlice.actions

export const mainViewSelector = (state: RootState) => state.decision.mainView
export const subViewSelector = (state: RootState) => state.decision.subView
export const programSelector = (state: RootState) => state.decision.program
export const creditProgramIDSelector = (state: RootState) => state.decision.creditProgramID
export const amountSelector = (state: RootState) => state.decision.loanParams?.amount
export const periodSelector = (state: RootState) => state.decision.loanParams?.period
export const okedIDSelector = (state: RootState) => state.decision.okedID
export const propertySelector = (state: RootState) => state.decision.property
export const insuranceSumSelector = (state: RootState) => state.decision.insuranceSum
export const creditSumSelector = (state: RootState) => state.decision.creditSum
export const loanParamsSelector = createSelector(amountSelector, periodSelector, (amount, period) => ({
  amount,
  period
}))
export const getInsuranceDataSelector = createSelector(
  insuranceSumSelector,
  creditSumSelector,
  (insuranceSum, creditSum) => ({
    insuranceSum,
    creditSum
  })
)

export default decisionSlice.reducer
